<template>
  <div class="main-wrapper">
    <div class="pannel-wrapper">
      <div class="area-wrapper">
        <div class="area-wrapper-header">
          <img
            src="@/assets/icons/newEyeOpenIcon.svg"
            alt="eye"
            class="eye"
            @click="hideAllMeasurement"
            v-if="areaMeasurIsShown && !anonymousUser"
          />
          <img
            src="@/assets/icons/newEyeClosedIcon.svg"
            alt="eye"
            class="eye-closed"
            @click="showAllMeasurement"
            v-if="!areaMeasurIsShown && !anonymousUser"
          />
          <div
            class="section-header-right-side"
            @click="toggleAreas"
            :class="{ inactiveGray: !areaMeasurIsShown }"
          >
            <p class="cluster-title">
              {{ $t("sidebar.areas") }}
            </p>
            <img
              src="@/assets/icons/newArrowUp.svg"
              alt="arrow"
              class="arrow"
              v-show="showAreasbool"
            />
            <img
              src="@/assets/icons/newArrowDown.svg"
              alt="arrow"
              class="arrow"
              v-show="!showAreasbool"
            />
          </div>
        </div>
        <div v-if="showAreasbool" class="area-details-box">
          <div
            class="area-inside-wrapper"
            v-for="(area, index) in completedAreaMeasurements"
            :key="index"
          >
            <div
              class="area-header"
              :class="{
                active: index == selectedArea && area.show,
                grayClass: !areaMeasurIsShown || !area.show,
                expand: index == selectedArea,
              }"
            >
              <template v-if="editingIndex !== index">
                <img
                  src="@/assets/icons/newEyeOpenIcon.svg"
                  alt="eye"
                  class="eye item-eye"
                  v-if="area.show && !anonymousUser"
                  @click="hideThisArea(area, index)"
                />
                <img
                  src="@/assets/icons/newEyeClosedIcon.svg"
                  alt="eye"
                  class="eye-closed item-eye"
                  @click="showThisArea(area, index)"
                  v-if="!area.show && !anonymousUser"
                />
                <div
                  @click="showareaDetails(area, index, true)"
                  class="left-text"
                >
                  <p>{{ area.name || defaultAreaName(index + 1, $t) }}</p>
                </div>
                <div
                  class="anotation-image-wrapper"
                  :class="{
                    'shown-areas': area.show && areaMeasurIsShown && !anonymousUser,
                  }"
                >
                  <img
                    src="@/assets/icons/edit-panel-dark.svg"
                    alt="edit"
                    class="edit-icon"
                    v-if="showTrashIcon && area.show && !anonymousUser && featureFlag('solar_object_renaming')"
                    @mousedown.prevent
                    @click="startEditing(area, index)"
                  />
                  <img
                    src="@/assets/icons/newTrashIcon.svg"
                    alt="trash"
                    class="trash"
                    v-if="showTrashIcon && area.show && !anonymousUser"
                    @click="handleAreaMeasurementDelete(area)"
                  />
                </div>
              </template>
              <div v-else class="edit-name-wrapper">
                <textarea
                  class="edit-name-input"
                  :placeholder="defaultAreaName(index + 1, $t)"
                  v-model="editNameValue"
                  rows="1"
                  @keydown.stop
                  @keydown.enter.exact.prevent
                  @keyup.stop
                  @keyup.enter="confirmRename(area, index)"
                  @keyup.esc="cancelRename"
                  @blur="confirmRename(area, index)"
                  @input="autoResize"
                  ref="editInput"
                ></textarea>
              </div>
            </div>
            <div class="area-details" v-if="index == selectedArea">
              <div class="details-text">
                <p>{{ $t("sidebar.area") }}</p>
                <span
                  >{{
                    isNaN(area.surfaceArea)
                      ? area.surfaceArea
                      : $n(Number(area.surfaceArea), "decimal")
                  }}m²</span
                >
              </div>
              <div class="details-text">
                <p>{{ $t("sidebar.slope") }}</p>
                <span
                  >{{
                    isNaN(area.angle) ? area.angle : $n(Number(area.angle), "decimal")
                  }}°</span
                >
              </div>
              <div class="details-text">
                <p>{{ $t("sidebar.perimeter") }}</p>
                <span>{{ $n(Number(activeAreaPerimeter), "decimal") }}m</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, nextTick, watch, computed } from "vue";
import { useStore } from "vuex";
import API from "@/api/API";
import { hasPermission, Permission } from "@/constants/permissions";
import { defaultAreaName } from "@/utils/annotation-names";

const store = useStore();
const isBobbieContext = computed(() => store.state.auth.isBobbieContext);
const featureFlag = (name) => store.getters["featureFlag"](name);

import {
  hideSingleMeasurementArea,
  showSingleMeasurementArea,
} from "@/modules/areaMeasurement.js";

const emits = defineEmits(["setColapsedSections"]);

const props = defineProps([
  "completedAreaMeasurements",
  "areaDetails",
  "hideAllMeasurementAreas",
  "showAllMeasurementAreas",
  "config",
  "setVisibilityInStore",
  "objectIsloaded",
  "selectedFromSidebarEvent",
  "removeSelectedMeasurementArea",
  "detectedMeasurementArea",
  "unselectFromSidebar",
  "anonymousUser",
  "active",
]);

const showAreasbool = ref(false);
const selectedArea = ref(null);
const showTrashIcon = ref(true);
const editingIndex = ref(null);
const editNameValue = ref("");
const editInput = ref(null);

function startEditing(area, index) {
  if (editingIndex.value !== null && editingIndex.value !== index) {
    confirmRename(props.completedAreaMeasurements[editingIndex.value], editingIndex.value);
  }
  editingIndex.value = index;
  editNameValue.value = area.name || "";
  nextTick(() => {
    if (editInput.value) {
      const input = Array.isArray(editInput.value) ? editInput.value[0] : editInput.value;
      if (input) {
        input.style.height = "auto";
        input.style.height = input.scrollHeight + "px";
        input.focus();
      }
    }
  });
}

function confirmRename(area, index) {
  if (editingIndex.value !== index) return;

  const newName = editNameValue.value.trim();
  if (newName) {
    area.name = newName;
    if (hasPermission(Permission.Update_Objects) && area.id) {
      API.airteam3DViewer.patchSolarAreaObject({ name: newName }, area.id);
    }
  } else if (area.name && featureFlag('solar_object_renaming')) {
    area.name = null;
    if (!props.sample && area.id) {
      API.airteam3DViewer.patchSolarAreaObject({ name: null }, area.id);
    }
  }
  editingIndex.value = null;
  editNameValue.value = "";
}

function cancelRename() {
  editingIndex.value = null;
  editNameValue.value = "";
}

function autoResize(event) {
  const el = event.target;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function toggleAreas() {
  if (props.areaDetails.length) {
    showAreasbool.value = !showAreasbool.value;
    emits("setColapsedSections", "areaComponent");
  }
  if (showAreasbool.value) {
    selectedArea.value = null;
  }
}

function colapseFromParent() {
  if (props.areaDetails.length) {
    showAreasbool.value = false;
    previousSelectedArea.value = null;
    if (props.completedAreaMeasurements[selectedArea.value]) {
      props.unselectFromSidebar(props.completedAreaMeasurements[selectedArea.value]);
    }
  }
}

function showareaDetails(area, number, fromSidebar = false) {
  if (!area.show || !areaMeasurIsShown.value) {
    selectedArea.value = null;
    props.unselectFromSidebar(area);
    return;
  }
  if (!showAreasbool.value) {
    showAreasbool.value = true;
    emits("setColapsedSections", "areaComponent");
  }
  sumUpPerimeter(area);

  if (selectedArea.value != number) {
    selectedArea.value = number;
    if (fromSidebar) {
      props.selectedFromSidebarEvent(area.label.id);
    }
  } else {
    if (fromSidebar) {
      selectedArea.value = null;
      props.unselectFromSidebar(area);
    }
  }
}

const activeAreaPerimeter = ref(null);

function sumUpPerimeter(area) {
  const sum = ref(0);
  area.lines.forEach((element) => {
    sum.value = sum.value + parseFloat(element.label.element.distance);
  });

  activeAreaPerimeter.value = sum.value.toFixed(2);
}

const areaMeasurIsShown = ref(false);

function hideAllMeasurement() {
  if (props.areaDetails[selectedArea.value]) {
    props.unselectFromSidebar(props.areaDetails[selectedArea.value]);
  }
  selectedArea.value = null;

  if (props.areaDetails.length) {
    props.hideAllMeasurementAreas();
    areaMeasurIsShown.value = false;
    props.setVisibilityInStore("measurements_visible", false);
  }
  showTrashIcon.value = false;
}
function showAllMeasurement() {
  props.showAllMeasurementAreas();
  areaMeasurIsShown.value = true;
  props.setVisibilityInStore("measurements_visible", true);
  showTrashIcon.value = true;
}

const hiddenArea = ref(null);

function hideThisArea(area, index) {
  selectedArea.value = null;
  props.unselectFromSidebar(area);

  area.show = false;
  if (areaMeasurIsShown.value) {
    hideSingleMeasurementArea(area);
  }
  hiddenArea.value = index;
  const show = false;
  setAreaAppearance(area.id, show);
}
function showThisArea(area, index) {
  area.show = true;
  if (areaMeasurIsShown.value) {
    showSingleMeasurementArea(area);
  }

  hiddenArea.value = null;
  const show = true;
  setAreaAppearance(area.id, show);
}

async function setAreaAppearance(id, show) {
  if (!hasPermission(Permission.Update_Objects)) {
    return;
  }
  return await API.airteam3DViewer.patchObject(id, show);
}

function setAreaAppearanceButton() {
  for (const area of props.areaDetails) {
    if (area.show) {
      areaMeasurIsShown.value = true;
      break; // Exit the loop
    }
  }
}

function deselectSidebarArea() {
  if (selectedArea.value !== null) {
    selectedArea.value = null;
  }
}

let previousSelectedArea = ref(null);
function selectMeasurementAreaFromCanvas(indexOfArea) {
  if (selectedArea.value != indexOfArea) {
    selectedArea.value = indexOfArea;
  }
  if (props.areaDetails.length) {
    if (!props.areaDetails[indexOfArea]?.show) {
      return;
    }
    showAreasbool.value = true;
    if (previousSelectedArea.value != indexOfArea) {
      emits("setColapsedSections", "areaComponent");
      previousSelectedArea.value = indexOfArea;
    }
    // Add scroll behavior
    setTimeout(() => {
      const selectedElement = document.querySelector(".area-header.active.expand");
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 100);
  }
}

function handleCreateAreaMeasurement() {
  areaMeasurIsShown.value = true;
  props.showAllMeasurementAreas();
  if (props.areaDetails.length) {
    showAreasbool.value = true;
  }
  if (showAreasbool.value) {
    emits("setColapsedSections", "areaComponent");
    props.setVisibilityInStore("measurements_visible", true);

    const lastArea = props.areaDetails[props.areaDetails.length - 1];
    showThisArea(lastArea, props.areaDetails.length - 1);
    showareaDetails(lastArea, props.areaDetails.length - 1);

    // Add scroll behavior
    setTimeout(() => {
      const selectedElement = document.querySelector(".area-header.active.expand");
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
      startEditing(lastArea, props.areaDetails.length - 1);
    }, 100);
  }
}
defineExpose({
  colapseFromParent,
  handleCreateAreaMeasurement,
  showMeasurementAreaFlag,
  deselectSidebarArea,
  selectMeasurementAreaFromCanvas,
  editingIndex,
});

function handleAreaMeasurementDelete(area) {
  props.selectedFromSidebarEvent(area.label.id, false);
  props.removeSelectedMeasurementArea();
}

function showMeasurementAreaFlag() {
  if (props.areaDetails.length) {
    areaMeasurIsShown.value = true;
    showAllMeasurement();
    props.setVisibilityInStore("measurements_visible", true);
  }
}

watch(
  () => props.completedAreaMeasurements[selectedArea.value]?.surfaceArea,
  (newValue) => {
    if (newValue) {
      sumUpPerimeter(props.completedAreaMeasurements[selectedArea.value]);
    }
  }
);

// Watch for changes in props.areaDetails
watch(
  () => props.areaDetails.length,
  (newLength) => {
    if (newLength > 0) {
      setAreaAppearanceButton();
    }
  }
);
// Watch for changes in props.detectedMeasurementArea
watch(
  () => props.detectedMeasurementArea,
  (newValue) => {
    if (newValue) {
      // Find the selected area
      let selectedArea = props.areaDetails.filter(
        (element) => element.label.id == newValue.id + 1
      );

      // Get the index of the selected area
      let selectedIndex = props.areaDetails.findIndex(
        (element) => element.label.id == newValue.id + 1
      );

      // Show the selected area details
      showareaDetails(...selectedArea, selectedIndex);

      // Add scroll behavior
      setTimeout(() => {
        const selectedElement = document.querySelector(".area-header.active.expand");
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }, 100);
    } else {
      selectedArea.value = null;
    }
  }
);

watch(
  () => props.objectIsloaded,
  (newValue) => {
    if (newValue) {
      if (props.config.measurements_visible) {
        areaMeasurIsShown.value = props.config.measurements_visible;
        showAllMeasurement();
      } else {
        hideAllMeasurement();
      }
    }
  }
);
</script>

<style lang="scss" scoped>
.main-wrapper {
  .pannel-wrapper {
    max-height: 31rem;
    overflow-y: scroll;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    .area-wrapper {
      .area-wrapper-header {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--color-dark-bg-secondary);
        max-height: 30px;
        display: flex;
        gap: 1rem;
        align-items: center;
        padding: 1rem 0;
        .section-header-right-side {
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: center;
        }
        .section-header-right-side.inactiveGray {
          p {
            color: gray;
          }
          img {
            filter: grayscale(1) brightness(0.5); /* Initial gray color */
          }
        }
        p {
          font-family: "Roboto";
          font-size: 14px;
          font-style: normal;
          font-weight: bold;
          line-height: 19px;
          color: white;
          letter-spacing: 0.03em;
        }
        img {
          height: 12px;
          width: 18px;
          cursor: pointer;
        }
        .arrow,
        .trash {
          height: 20px;
          width: 20px;
        }
      }
      .area-details-box {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 12px;
        margin-top: 6px;

        .area-inside-wrapper {
          .area-header.active.expand {
            border: 1px solid var(--color-gree300);
            border-radius: 10px 10px 0 0;
            border-bottom: none;
            margin-bottom: 1px;
            .anotation-image-wrapper {
              display: flex;
            }
          }
          .area-header.active:hover {
            .anotation-image-wrapper {
              img {
                filter: brightness(0) invert(1); /* White color on hover */
              }
            }
          }
          .area-header.expand {
            border: none;
            margin-bottom: 1px;
            border-radius: 10px 10px 0 0;
          }
          .area-header:not(.active):hover {
            .anotation-image-wrapper.shown-areas {
              display: flex;
              .trash,
              .edit-icon {
                filter: grayscale(1) brightness(1.7);
              }
              img:hover {
                filter: brightness(0) invert(1); /* White color on hover */
              }
            }
          }
          .area-header {
            background: var(--color-dark-bg-primary);
            // border: 1px solid var(--color-dark-bg-tertiary);
            display: flex;
            gap: 8px;
            align-items: center;
            padding: 1rem 0.5rem;
            max-height: 30px;
            border-radius: 10px;
            display: flex;
            justify-content: space-between;
            &:has(.edit-name-wrapper) {
              max-height: none;
            }
            margin-bottom: 1px;
            .left-text {
              flex: 1;
              width: 0;
              min-width: 0;
              overflow: hidden;
              p {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
            .edit-name-wrapper {
              flex: 1;
              min-width: 0;
              overflow: hidden;
              .edit-name-input {
                width: 100%;
                box-sizing: border-box;
                background: #b6b6b61a;
                border: none;
                border-radius: 4px;
                color: white;
                font-family: "Roboto";
                font-size: 14px;
                padding: 2px 6px;
                outline: none;
                resize: none;
                overflow: hidden;
                overflow-wrap: break-word;
                word-break: break-word;
                line-height: 19px;
                &::placeholder {
                  color: gray;
                }
              }
            }
            .anotation-image-wrapper {
              display: none;
              gap: 8px;
            }
            .edit-icon {
              margin-top: 2px;
            }
            .item-eye {
              width: 16px;
              height: 16px;
              cursor: pointer;
              flex-shrink: 0;
            }
            p.grayCustome {
              color: gray;
            }
            p {
              color: white;
              cursor: pointer;
            }
            img {
              width: 16px;
              height: 16px;
              cursor: pointer;
              filter: brightness(0) invert(1);
            }
          }
          .area-details {
            background: var(--color-dark-bg-primary);
            border: 1px solid var(--color-gree300);
            border-top: none;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
            padding: 5px 5px 8px 10px;
            .details-text {
              display: flex;
              justify-content: space-between;
            }
          }

          .area-header.grayClass {
            margin-bottom: 1px;
            p {
              color: gray;
            }
            .item-eye {
              filter: grayscale(1) brightness(0.7);
            }
            .item-eye.eye-closed {
              filter: grayscale(1) brightness(1.3);
            }
            .item-eye:hover {
              filter: brightness(0) invert(1);
            }
          }
          .area-header.active.grayClass {
            border: none;
            .item-eye {
              filter: brightness(0) invert(0.5);
            }
            .item-eye:hover {
              filter: brightness(0) invert(1);
            }
          }
          .area-details.grayClass {
            border: none;

            p,
            span {
              color: gray;
            }
          }
        }
      }
    }
  }
}
.eye:hover {
  filter: brightness(0) invert(1); /* White color on hover */
}
.eye-closed {
  filter: grayscale(1) brightness(1);
}

@media (max-width: 1600px) {
  .main-wrapper {
    .pannel-wrapper {
      .area-wrapper {
        .area-details-box {
          .area-inside-wrapper {
            .area-details {
              border: 1px solid var(--color-gree300);
              border-top: none;
              border-bottom-left-radius: 10px;
              border-bottom-right-radius: 10px;
              padding: 5px 5px 8px 10px;
              p {
                font-size: 12px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
